"use client";
import { Suspense, useState } from "react";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";

import { Button } from "@heroui/button";
import { z } from "zod";

import { PageFormTemplate } from "@/components/forms/components/PageFormTemplate";
import { PageViewTracker } from "@/components/observability/PageViewTracker";
import { verifyEmailAction } from "@/lib/actions/auth";
import { handleResendVerificationAction } from "@/lib/actions/resend-verification";
import { EventKeys } from "@/lib/observability/catalog";
import { routes } from "@/lib/routes";

const verifyEmailSchema = z.object({
  email: z
    .string()
    .min(1, "Invalid or missing email.")
    .email("Invalid email address."),
  token: z.string().min(1, "Invalid or missing token."),
});

type VerifyEmailFormData = z.infer<typeof verifyEmailSchema>;

function shouldShowResendForError(error?: string): boolean {
  return Boolean(!error ? false : error.toLowerCase().includes("expired"));
}

async function resendVerificationForEmail(
  email: string,
): Promise<{ message: string; success: boolean }> {
  const result = await handleResendVerificationAction({ email });

  if (result.success) {
    return {
      message: "A new verification email has been sent to your email address.",
      success: true,
    };
  }

  return {
    message: result.error ?? "Failed to send verification email.",
    success: false,
  };
}

function InvalidVerificationLinkState({
  email,
  isResending,
  onBackToLogin,
  onOpenResendPage,
  onResend,
  resendMessage,
}: {
  email: string;
  isResending: boolean;
  resendMessage: string;
  onResend: () => void;
  onBackToLogin: () => void;
  onOpenResendPage: () => void;
}) {
  return (
    <div className="bg-default-50" data-testid="verify-email-page">
      <div className="w-full max-w-lg mx-auto py-16 px-4 sm:px-0">
        <div className="p-6 bg-white rounded-lg shadow-sm border border-default-200 max-w-md w-full mx-auto">
          <div className="bg-danger-50 border border-danger-200 rounded-md p-4 text-danger-800 mb-4">
            <div className="font-bold mb-1">Invalid verification link</div>
            <div className="mb-2">
              This verification link is missing required information. Please
              check your email for the correct link or request a new one.
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {email ? (
              <>
                <Button
                  color="primary"
                  data-testid="resend-verification-inline-button"
                  isLoading={isResending}
                  onPress={onResend}
                >
                  {isResending ? "Sending..." : "Send Verification Email Again"}
                </Button>
                {resendMessage && (
                  <div className="text-sm text-success-700">
                    {resendMessage}
                  </div>
                )}
              </>
            ) : (
              <Button color="primary" onPress={onOpenResendPage}>
                Resend Verification Email
              </Button>
            )}
            <Button
              color="default"
              data-testid="back-to-login-button"
              variant="light"
              onPress={onBackToLogin}
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VerifyEmailContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const email = searchParams.get("email") ?? "";
  const [showResend, setShowResend] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [isResending, setIsResending] = useState(false);

  // Event keys for observability
  const eventKeys = {
    attempt: EventKeys.AUTH_VERIFY_EMAIL_PAGE_VIEW,
    failure: EventKeys.AUTH_VERIFY_EMAIL_PAGE_VIEW,
    success: EventKeys.AUTH_VERIFY_EMAIL_PAGE_VIEW,
  };

  // Custom submit handler to handle expired/error states
  const onSubmit = async (data: { token: string; email: string }) => {
    const result = await verifyEmailAction(data.token, data.email);

    if (!result.success && shouldShowResendForError(result.error)) {
      setShowResend(true);
    }

    return result;
  };

  const handleResend = async () => {
    setIsResending(true);
    setResendMessage("");
    const result = await resendVerificationForEmail(email);

    setIsResending(false);
    setResendMessage(result.message);
  };

  // If token or email is missing, show an error alert instead of the form
  if (!token || !email) {
    return (
      <InvalidVerificationLinkState
        email={email}
        isResending={isResending}
        resendMessage={resendMessage}
        onBackToLogin={() => router.push(routes.auth.login())}
        onOpenResendPage={() => router.push(routes.auth.resendVerification())}
        onResend={() => {
          void handleResend();
        }}
      />
    );
  }

  return (
    <div className="bg-default-50">
      <div className="w-full max-w-lg mx-auto py-16 px-4 sm:px-0">
        <PageFormTemplate<VerifyEmailFormData>
          data-testid="verify-email-page"
          defaultValues={{ email, token }}
          description="Confirm your email address to activate your account."
          eventKeys={eventKeys}
          formSchema={verifyEmailSchema}
          icon="MailCheck"
          submitLabel="Verify Email"
          title="Verify Your Email"
          onSubmit={onSubmit}
          onSuccess={() => router.push(routes.auth.login())}
        >
          {() => (
            <>
              {/* Info box at the top */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800 mb-4">
                <p className="font-medium mb-1">Why verify your email?</p>
                <p className="mb-0">
                  Verifying your email helps us keep your account secure and
                  lets you access all features. If your link is expired, you can
                  request a new one below.
                </p>
              </div>

              {/* Hidden fields for token and email */}
              <input name="token" type="hidden" value={token} />
              <input name="email" type="hidden" value={email} />

              {showResend && (
                <div className="my-4">
                  <Button
                    color="primary"
                    data-testid="resend-verification-button"
                    isLoading={isResending}
                    onPress={() => {
                      void handleResend();
                    }}
                  >
                    {isResending ? "Sending..." : "Send New Verification Email"}
                  </Button>
                  {resendMessage && (
                    <div className="mt-2 text-sm text-success-700">
                      {resendMessage}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 text-center text-sm">
                <Link
                  className="text-primary"
                  data-testid="back-to-login-button"
                  href={routes.auth.login()}
                >
                  Back to login
                </Link>
              </div>
            </>
          )}
        </PageFormTemplate>
      </div>
    </div>
  );
}

export default function VerifyEmailPage() {
  return (
    <>
      <PageViewTracker eventKey={EventKeys.PUBLIC_VERIFY_EMAIL_PAGE_VIEW} />
      <Suspense
        fallback={
          <div className="bg-default-50">
            <div className="w-full max-w-lg mx-auto py-16 px-4 sm:px-0">
              <div className="p-6 bg-white rounded-lg shadow-sm border border-default-200 max-w-md w-full mx-auto">
                <div className="text-center">Loading...</div>
              </div>
            </div>
          </div>
        }
      >
        <VerifyEmailContent />
      </Suspense>
    </>
  );
}
